/**
 * Bridge between the app's slide navigation and an `html` slide's in-slide build steps.
 *
 * Every way of advancing a slide — keyboard, the on-screen ‹ › arrows, remote control, swipe, the
 * EventBus — funnels through SlideWrapper.selectNextSlide / selectPrevSlide. Those call tryStepBuild
 * FIRST: if the active html slide still has an entrance build to reveal, it consumes the press and the
 * deck stays put; otherwise navigation proceeds as normal. This makes builds advance under Preview
 * and Present regardless of how the user clicks/keys, not just via the keyboard.
 *
 * The active HtmlSlideCastingView registers its stepper while mounted and clears it on destroy, so at
 * most one slide is ever the target. A miss (no active html slide, or not in a presenting mode)
 * returns false, leaving navigation untouched.
 */

let activeStepper = null
let lastNavDir = 1

// The active html casting view registers a `(dir) => boolean` stepper (true = a build was consumed).
export function registerBuildStepper(fn) {
  activeStepper = fn
}

export function unregisterBuildStepper(fn) {
  if (activeStepper === fn) activeStepper = null
}

// Direction of the last PRESENTER navigation, so the slide we land on next seats its builds bare
// (forward) or fully-built (back) — see HtmlSlideCastingView.setupBuilds.
export function getLastNavDir() {
  return lastNavDir
}

// Recorded by the stepper itself (which knows it's presenting) so plain editor navigation never
// touches it — otherwise arrowing around the editor before Preview would seat the slide built and
// swallow its entrance animation.
export function setLastNavDir(dir) {
  lastNavDir = dir < 0 ? -1 : 1
}

// Called by slide navigation before crossing slides. Asks the active html slide to consume a build
// step; returns true when it did (caller must NOT change slide). The stepper records the direction
// only when presenting, so this is a pure no-op outside a presenting context.
export function tryStepBuild(dir) {
  return !!(activeStepper && activeStepper(dir))
}
